import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useIsPreloaderVisible, useHidePreloader } from "@/stores/uiStore";

// Reduced timeout for better Lighthouse scores (was 7000ms)
const PRELOADER_TIMEOUT = 2000;
const MIN_DISPLAY_TIME = 100;
const SMOOTH_EASE = [0.3, 0, 0.3, 1] as const;

// Detect if running in prerender/headless mode (Lighthouse, bots)
const isPrerenderMode = () => {
  if (typeof window === "undefined") return true;
  // Check for common headless/prerender indicators
  const navigator = window.navigator as Navigator & { webdriver?: boolean };
  return (
    navigator.webdriver === true ||
    document.documentElement.hasAttribute("data-prerender") ||
    // Playwright/prerender sets this
    !window.matchMedia("(prefers-reduced-motion: no-preference)").matches
  );
};

export default function Preloader() {
  const isVisible = useIsPreloaderVisible();
  const hidePreloader = useHidePreloader();
  const [isLoaded, setIsLoaded] = useState(false);
  const [shouldRender, setShouldRender] = useState(true);
  const startTime = useRef(Date.now());

  // Skip preloader entirely in prerender/headless mode for Lighthouse
  useEffect(() => {
    if (isPrerenderMode()) {
      hidePreloader();
      setShouldRender(false);
      return;
    }
  }, [hidePreloader]);

  useEffect(() => {
    // Don't wait for images in prerender mode
    if (isPrerenderMode()) return;

    const checkImages = () => {
      const images = document.querySelectorAll("img");
      if (images.length === 0) {
        setIsLoaded(true);
        return;
      }

      let loadedCount = 0;
      const totalImages = images.length;

      const onImageLoad = () => {
        loadedCount++;
        if (loadedCount >= totalImages) {
          setIsLoaded(true);
        }
      };

      images.forEach((img) => {
        if (img.complete) {
          loadedCount++;
        } else {
          img.addEventListener("load", onImageLoad);
          img.addEventListener("error", onImageLoad);
        }
      });

      if (loadedCount >= totalImages) {
        setIsLoaded(true);
      }

      return () => {
        images.forEach((img) => {
          img.removeEventListener("load", onImageLoad);
          img.removeEventListener("error", onImageLoad);
        });
      };
    };

    const timer = setTimeout(checkImages, 50);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isPrerenderMode()) return;
    if (isLoaded) {
      const elapsed = Date.now() - startTime.current;
      const remaining = Math.max(0, MIN_DISPLAY_TIME - elapsed);

      const timer = setTimeout(() => {
        hidePreloader();
      }, remaining);

      return () => clearTimeout(timer);
    }
  }, [isLoaded, hidePreloader]);

  useEffect(() => {
    if (isPrerenderMode()) return;
    const timeout = setTimeout(hidePreloader, PRELOADER_TIMEOUT);
    return () => clearTimeout(timeout);
  }, [hidePreloader]);

  const handleExitComplete = () => {
    setShouldRender(false);
  };

  if (!shouldRender) return null;

  return (
    <AnimatePresence onExitComplete={handleExitComplete}>
      {isVisible && (
        <div className="fixed inset-0 z-100 flex items-center justify-center pointer-events-none">
          <motion.div
            className="absolute inset-0 bg-background origin-top"
            initial={{ scaleY: 1, skewY: 0 }}
            exit={{
              scaleY: 0,
              skewY: -5,
              transition: {
                duration: 0.8,
                ease: SMOOTH_EASE,
                delay: 0.3,
              },
            }}
          />

          <motion.div
            className="relative z-10 flex flex-col items-center gap-8"
            initial={{ opacity: 1 }}
            exit={{
              opacity: 0,
              transition: {
                duration: 0.5,
                ease: SMOOTH_EASE,
              },
            }}
          >
            <img
              src="/assets/images/logo.svg"
              alt="Afi"
              className="w-[120px] h-auto"
            />

            <div className="relative w-[120px] h-0.5 bg-white/10 overflow-hidden">
              <motion.div
                className="absolute inset-0 bg-accent"
                initial={{ x: "-100%" }}
                animate={{
                  x: ["-100%", "0%", "100%"],
                }}
                transition={{
                  duration: 1.5,
                  ease: SMOOTH_EASE,
                  repeat: Infinity,
                  repeatType: "loop",
                }}
              />
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
